import { getAccessToken } from './auth';

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function apiCall<T>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: unknown
): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type':  'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let err: any = {};
    try { err = await res.json(); } catch { err = { error: res.statusText }; }
    throw new ApiError(res.status, err.error ?? 'Request failed', err.code);
  }
  return res.json();
}

// ── Typed endpoint helpers ────────────────────────────────────────────────────

export interface ReportMeta {
  rowCount: number;
  durationMs: number;
  stagedAt: string | null;
  isStale: boolean;
  ageHours: number | null;
}

export interface ReportResult {
  rows: Record<string, any>[];
  columns: string[];
  chartType: 'bar' | 'line' | 'stacked' | 'table' | 'pie';
  meta: ReportMeta;
}

export interface TemplateMeta {
  key: string;
  name: string;
  description: string;
  chartType: ReportResult['chartType'];
  dataSource: 'warehouse' | 'odbc';
  primaryTable: string;
  filters: Array<{
    key: string;
    label: string;
    type: 'dateRange' | 'multiSelect' | 'singleSelect' | 'number' | 'text';
    required: boolean;
    defaultValue?: unknown;
    options?: Array<{ value: string; label: string }>;
  }>;
}

export const api = {
  listTemplates: () => apiCall<{ templates: TemplateMeta[] }>('/api/report/templates'),

  runTemplate: (key: string, params: Record<string, unknown>) =>
    apiCall<ReportResult>(`/api/report/templates/${key}/run`, 'POST', { params }),

  buildReport: (messages: Array<{ role: 'user' | 'assistant'; content: string }>) =>
    apiCall<
      | { type: 'question'; question: string }
      | (ReportResult & { type: 'result'; sql: string; suggestedName: string })
      | { type: 'error'; error: string; code: string }
    >('/api/report/build', 'POST', { messages }),

  listSavedReports: (search?: string, type?: 'template' | 'custom') => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (type)   params.set('type', type);
    const qs = params.toString();
    return apiCall<{ reports: any[] }>(`/api/reports/saved${qs ? '?' + qs : ''}`);
  },

  saveReport: (body: {
    name: string;
    description?: string;
    reportType: 'template' | 'custom';
    templateKey?: string;
    sqlQuery?: string;
    params?: Record<string, unknown>;
    chartType?: string;
    isPublic?: boolean;
  }) => apiCall<{ id: string }>('/api/reports/saved', 'POST', body),

  updateSavedReport: (id: string, body: any) =>
    apiCall<{ ok: true }>(`/api/reports/saved/${id}`, 'PUT', body),

  deleteSavedReport: (id: string) =>
    apiCall<{ ok: true }>(`/api/reports/saved/${id}`, 'DELETE'),

  runSavedReport: (id: string, paramOverrides?: Record<string, unknown>) =>
    apiCall<ReportResult>(`/api/report/run/${id}`, 'POST', { paramOverrides }),

  getDashboards: () =>
    apiCall<{ org: any | null; personal: any | null }>('/api/dashboards'),

  addWidget: (dashboardId: string, body: { reportId: string; gridX?: number; gridY?: number; gridW?: number; gridH?: number }) =>
    apiCall<{ id: string }>(`/api/dashboards/${dashboardId}/widgets`, 'POST', body),

  removeWidget: (dashboardId: string, widgetId: string) =>
    apiCall<{ ok: true }>(`/api/dashboards/${dashboardId}/widgets/${widgetId}`, 'DELETE'),

  updateLayout: (dashboardId: string, layout: any[]) =>
    apiCall<{ ok: true }>(`/api/dashboards/${dashboardId}/layout`, 'PUT', { layout }),

  listSchedules: () =>
    apiCall<{ schedules: any[] }>('/api/schedules'),

  createSchedule: (body: any) =>
    apiCall<{ id: string; nextRunAt: string }>('/api/schedules', 'POST', body),

  updateSchedule: (id: string, body: any) =>
    apiCall<{ ok: true; nextRunAt: string }>(`/api/schedules/${id}`, 'PUT', body),

  deleteSchedule: (id: string) =>
    apiCall<{ ok: true }>(`/api/schedules/${id}`, 'DELETE'),

  runScheduleNow: (id: string) =>
    apiCall<{ ok: true }>(`/api/schedules/${id}/run-now`, 'POST'),

  getFeedToday: () =>
    apiCall<any>('/api/feed/today'),

  adminListUsers: (role?: string) =>
    apiCall<{ users: any[] }>(`/api/admin/users${role ? '?role=' + role : ''}`),

  adminApprove:    (id: string) => apiCall<{ ok: true }>(`/api/admin/users/${id}/approve`,    'POST'),
  adminDeny:       (id: string) => apiCall<{ ok: true }>(`/api/admin/users/${id}/deny`,       'POST'),
  adminPromote:    (id: string) => apiCall<{ ok: true }>(`/api/admin/users/${id}/promote`,    'POST'),
  adminDeactivate: (id: string) => apiCall<{ ok: true }>(`/api/admin/users/${id}/deactivate`, 'POST'),
};
